import React from 'react';
import { useDesign } from '../../context/DesignContext';
import { useToolExecution } from '../../hooks/useToolExecution';
import { FileUpload } from '../../components/ui/FileUpload';
import { RedesignCanvas } from '../../components/tools/RedesignCanvas';
import { ResultViewer } from '../../components/ResultViewer';
import { Button } from '../../components/ui/Button';
import { ToolType } from '../../types';
import { ToolHeader } from '../../components/ui/ToolHeader';
import { TOOL_COSTS } from '../../constants';

interface GenericToolProps {
  toolId: ToolType;
}

const TOOL_INFO = {
  redesign: {
    title: "Redesign Tool",
    cost: `${TOOL_COSTS.redesign} credits`,
    description: (
      <>
        <p className="font-bold">Modify specific parts of an image using AI masking.</p>
        <p>1. <strong>Optional:</strong> Use the <span className="text-red-600 font-bold">Red Brush</span> and <span className="text-green-600 font-bold">Green Brush</span> to select areas.</p>
        <p>2. Enter separate instructions for each masked area.</p>
      </>
    )
  },
  clone: {
    title: "Design Extractor",
    cost: `${TOOL_COSTS.clone} credits`,
    description: (
      <>
        <p className="font-bold">Extract high-quality designs from product photos.</p>
        <p>Upload a photo of a T-shirt or product. The AI will extract just the graphic design, remove the background, and provide a clean PNG file ready for printing.</p>
      </>
    )
  },
  embroidery: {
    title: "Embroidery Simulator",
    cost: `${TOOL_COSTS.embroidery} credits`,
    description: "Transform your designs into realistic embroidery. Upload your design and the AI will simulate stitches and texture."
  },
  removebg: {
    title: "Background Remover",
    cost: `${TOOL_COSTS.removebg} credits`,
    description: "Remove backgrounds from your images instantly."
  },
  upscale: {
    title: "Image Upscaler",
    cost: `${TOOL_COSTS.upscale} credits`,
    description: "Increase image resolution while maintaining quality."
  }
};

export const GenericTool: React.FC<GenericToolProps> = ({ toolId }) => {
  const { 
    inputImage, setInputImage, 
    generalInstruction, setGeneralInstruction, 
    redInstruction, setRedInstruction, 
    greenInstruction, setGreenInstruction, 
    maskData, setMaskData,
    apiResult, setApiResult,
    loading 
  } = useDesign();

  const { executeTool } = useToolExecution();

  const handleFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setInputImage(e.target?.result as string);
      setApiResult(null);
      setMaskData(null); // Clear any existing mask when new image is uploaded
    };
    reader.readAsDataURL(file);
  };

  const currentTool = TOOL_INFO[toolId as keyof typeof TOOL_INFO];

  return (
    <div>
      <ToolHeader 
        title={currentTool?.title || toolId}
        cost={currentTool?.cost}
        description={currentTool?.description}
      />
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Upload Image</label>
          <FileUpload onFileSelect={handleFileSelect} previewUrl={inputImage} />
        </div>

        {toolId === 'redesign' && inputImage && (
          <div className="space-y-4 border-t pt-4">
            <RedesignCanvas imageUrl={inputImage} onMaskGenerated={setMaskData} />
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {(maskData?.redMask || maskData?.greenMask) ? "Global Context (Optional)" : "Edit Instruction"}
                </label>
                <textarea 
                  value={generalInstruction} 
                  onChange={(e) => setGeneralInstruction(e.target.value)} 
                  placeholder={(maskData?.redMask || maskData?.greenMask) ? "E.g., Make it look like a vintage poster" : "E.g., Change the background to a beach, or Remove the person on the left"} 
                  className="w-full h-24 px-3 py-2 border rounded-md text-sm" 
                />
              </div>
              
              {maskData?.redMask && (
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">Red Mask Instruction (Required)</label>
                  <textarea 
                    value={redInstruction} 
                    onChange={(e) => setRedInstruction(e.target.value)} 
                    placeholder="What should appear in the red masked area?" 
                    className="w-full h-24 px-3 py-2 border border-red-300 rounded-md text-sm" 
                  />
                </div>
              )}

              {maskData?.greenMask && (
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">Green Mask Instruction (Required)</label>
                  <textarea 
                    value={greenInstruction} 
                    onChange={(e) => setGreenInstruction(e.target.value)} 
                    placeholder="What should appear in the green masked area?" 
                    className="w-full h-24 px-3 py-2 border border-green-300 rounded-md text-sm" 
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <Button 
          onClick={() => executeTool(toolId)} 
          className="w-full py-3 text-lg" 
          isLoading={loading} 
          disabled={!inputImage}
        >
          Start Processing
        </Button>
        
        {apiResult && (
          <ResultViewer 
            success={apiResult.success}
            error={apiResult.error}
            imageUrl={apiResult.image}
            images={apiResult.images}
            optimizedInstruction={apiResult.optimizedInstruction}
          />
        )}
      </div>
    </div>
  );
};
